import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { acknowledgementsApi, policiesApi, videosApi } from '../services/api';
import UserManagement from './admin/UserManagement';
import PolicyManagement from './admin/PolicyManagement';
import VideoManagement from './admin/VideoManagement';
import TagManagement from './admin/TagManagement';
import ApiTokenManagement from './admin/ApiTokenManagement';
import ActivityLogs from './admin/ActivityLogs';
import AcknowledgementManagement from './admin/AcknowledgementManagement';
import './HRHub.css';

/* ============================================================
   Icons used by the HR Hub shell.
   ============================================================ */
const P = {
  grid: ['M4 4h7v7H4zM13 4h7v7h-7zM13 13h7v7h-7zM4 13h7v7H4z'],
  file: ['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z', 'M14 3v5h5'],
  flag: ['M5 21V4M5 4c4-2 8 2 12 0v9c-4 2-8-2-12 0'],
  layers: ['M12 3 3 8l9 5 9-5-9-5ZM3 13l9 5 9-5M3 16.5l9 5 9-5'],
  video: ['M3 6a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v2l4-2v12l-4-2v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'],
  image: ['M4 5h16v14H4zM8.5 11a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M4 16l4-4 4 4 3-3 5 5'],
  dollar: ['M12 2.5v19M16.5 6.5c0-1.7-2-3-4.5-3S7.5 4.8 7.5 6.5 9.5 9.5 12 9.5s4.5 1.3 4.5 3-2 3-4.5 3-4.5-1.3-4.5-3'],
  folder: ['M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'],
  check: ['M5 12.5l4.5 4.5L20 6.5'],
  alert: ['M12 8v5M12 16.5v.5M10.3 3.9 2.7 17a1.5 1.5 0 0 0 1.3 2.3h16a1.5 1.5 0 0 0 1.3-2.3L13.7 3.9a1.5 1.5 0 0 0-2.6 0Z'],
  search: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM20 20l-4-4'],
  chevronRight: ['M9 5l7 7-7 7'],
  chevronLeft: ['M15 18l-6-6 6-6'],
  external: ['M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5'],
  download: ['M12 3v12M7 10l5 5 5-5M4 20h16'],
  signout: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9'],
  play: ['M8 5.5v13l11-6.5z'],
  close: ['M6 6l12 12M18 6L6 18'],
  users: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8', 'M22 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
  tag: ['M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z', 'M7 7h.01'],
  activity: ['M22 12h-4l-3 9L9 3l-3 9H2'],
  key: ['M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z', 'M16.5 7.5h.01'],
  clipboardCheck: ['M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2', 'M9 5a3 3 0 0 1 6 0M9 5h6', 'M8.5 13l2.5 2.5L16 10'],
  settings: [
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z',
  ],
};

function Icon({ name, size = 18, stroke = 1.75, fill = false, style }) {
  const paths = P[name] || [];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill ? 'currentColor' : 'none'}
      stroke={fill ? 'none' : 'currentColor'}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: 'none', ...style }}
      aria-hidden="true"
    >
      {paths.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}

/* ---------- Video source helpers ---------- */
function getEmbedUrl(url) {
  if (!url) return null;
  if (url.includes('youtube.com/watch')) {
    const id = url.split('v=')[1]?.split('&')[0];
    return id ? `https://www.youtube.com/embed/${id}` : null;
  }
  if (url.includes('youtu.be/')) {
    const id = url.split('youtu.be/')[1]?.split('?')[0];
    return id ? `https://www.youtube.com/embed/${id}` : null;
  }
  if (url.includes('vimeo.com/')) {
    const id = url.split('vimeo.com/')[1]?.split('?')[0];
    return id ? `https://player.vimeo.com/video/${id}` : null;
  }
  return null;
}

/* ---------- Small helpers ---------- */
const REQUIRED_RE = /required|mandatory/i;
const isRequired = (p) => (p.tags || []).some((t) => REQUIRED_RE.test(t?.name || ''));
const TAG_ACRONYMS = new Set(['api', 'hr', 'hrms', 'pf', 'it']);
const folderLabel = (name) => String(name || '')
  .trim()
  .split(/\s+/)
  .map((word) => {
    const lower = word.toLowerCase();
    if (TAG_ACRONYMS.has(lower)) return lower.toUpperCase();
    if (word === '&') return word;
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  })
  .join(' ');
const isDefaultFolder = (name) =>
  !name || /^(uncategor\w*|ungrouped|general|default|none|no folder|misc|documents)$/i.test(String(name).trim());

function getInitials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'U';
}
function fileTypeOf(p) {
  const src = p.file_name || p.blob_name || p.file_url || '';
  const m = String(src).match(/\.([a-z0-9]{2,5})(?:\?|$)/i);
  return m ? m[1].toUpperCase() : 'PDF';
}
function isImageFile(p) {
  return new Set(['AVIF', 'BMP', 'GIF', 'JPG', 'JPEG', 'PNG', 'TIF', 'TIFF', 'WEBP']).has(fileTypeOf(p));
}
function monthYear(d) {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date)) return '';
  return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
}
function docMeta(p) {
  return [fileTypeOf(p), p.created_at ? 'Updated ' + monthYear(p.created_at) : null]
    .filter(Boolean)
    .join('  ·  ');
}
/* ---------- Category taxonomy ----------
   Resource tabs represent what the item is. Tags are separate topic filters. */
const CATS = [
  { id: 'all', label: 'All resources', icon: 'grid' },
  { id: 'documents', label: 'Documents', icon: 'file' },
  { id: 'policies', label: 'Company policies', icon: 'flag' },
  { id: 'guides', label: 'User guides', icon: 'layers' },
  { id: 'videos', label: 'Video tutorials', icon: 'video' },
  { id: 'backgrounds', label: 'Virtual backgrounds', icon: 'image' },
  { id: 'tax-pf', label: 'Tax & PF', icon: 'dollar' },
];

function classify(text) {
  const s = String(text || '').toLowerCase();
  if (/virtual background|background|wallpaper/.test(s)) return 'backgrounds';
  if (/tax\s*(?:&|and)\s*pf|pf\s*(?:&|and)\s*tax|provident fund/.test(s)) return 'tax-pf';
  if (/polic|code of conduct|compliance/.test(s)) return 'policies';
  if (/guide|manual|walkthrough|how ?-?to|hrms|timesheet|mediclaim|tutorial|login|demo|setup/.test(s)) return 'guides';
  return null;
}
const typeOfPolicy = (p) => classify([
  ...(p?.tags || []).map((tag) => tag?.name),
  p?._folder,
  p?.title,
  p?.description,
].filter(Boolean).join(' ')) || 'documents';
const VIDEO_RESOURCE_TYPES = new Set(['policies', 'guides', 'videos', 'tax-pf']);
const typeOfVideo = (video) => {
  const taggedType = classify([
    ...(video?.tags || []).map((tag) => tag?.name),
    video?.title,
    video?.description,
  ].filter(Boolean).join(' '));
  return VIDEO_RESOURCE_TYPES.has(taggedType) ? taggedType : 'videos';
};
const normalizeResourceTag = (name) => String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');
const TAB_FALLBACKS = {
  policies: { id: 'company-policies', name: 'Company policies' },
  guides: { id: 'general-user-guides', name: 'General user guides' },
  backgrounds: { id: 'professional-virtual-backgrounds', name: 'Professional virtual backgrounds' },
  'tax-pf': { id: 'tax-pf-assistance', name: 'Tax & PF assistance' },
};
const topicTags = (resource) => (resource?.tags || []).filter((tag) => tag?.name && !REQUIRED_RE.test(tag.name));
/* ---------- Admin panel (rendered inside the HR Hub frame) ---------- */
const ADMIN_ITEMS = [
  { id: 'users', label: 'User Management', icon: 'users' },
  { id: 'policies', label: 'Policy Management', icon: 'file' },
  { id: 'videos', label: 'Video Management', icon: 'video' },
  { id: 'tags', label: 'Resource Tab', icon: 'tag' },
  { id: 'acknowledgements', label: 'Acknowledgements', icon: 'clipboardCheck' },
  { id: 'api-tokens', label: 'API Tokens', icon: 'key' },
  { id: 'logs', label: 'Activity Logs', icon: 'activity' },
];
const ADMIN_COMPONENTS = {
  users: UserManagement,
  policies: PolicyManagement,
  videos: VideoManagement,
  tags: TagManagement,
  acknowledgements: AcknowledgementManagement,
  'api-tokens': ApiTokenManagement,
  logs: ActivityLogs,
};

function HRHub() {
  const { user, isAdmin, logout } = useAuth();

  const [folders, setFolders] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const resourceFetchId = useRef(0);

  const [activeCat, setActiveCat] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('name');
  const [navOpen, setNavOpen] = useState(() => (typeof window !== 'undefined' ? window.innerWidth > 768 : true));
  const [adminView, setAdminView] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const [expandedResourceFolders, setExpandedResourceFolders] = useState(() => new Set());

  const [activeVideo, setActiveVideo] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  const [ackOpen, setAckOpen] = useState(false);
  const [hasAcknowledged, setHasAcknowledged] = useState(null);
  const [ackSaving, setAckSaving] = useState(false);
  const [ackError, setAckError] = useState('');

  useEffect(() => {
    if (!profileOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setProfileOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [profileOpen]);

  const refreshPublishedResources = useCallback(async () => {
    const id = ++resourceFetchId.current;
    try {
      const [publishedFolders, publishedVideos] = await Promise.all([
        policiesApi.getPublished(),
        videosApi.getPublished(),
      ]);
      if (id !== resourceFetchId.current) return;
      if (!Array.isArray(publishedFolders) || !Array.isArray(publishedVideos)) {
        throw new Error('Invalid resource data from server');
      }
      setFolders(publishedFolders);
      setVideos(publishedVideos);
      setError(null);
    } catch (err) {
      if (id === resourceFetchId.current) setError(err.message || 'Failed to load resources');
      throw err;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const id = ++resourceFetchId.current;
    Promise.all([policiesApi.getPublished(), videosApi.getPublished()])
      .then(([f, v]) => {
        if (!alive || id !== resourceFetchId.current) return;
        if (!Array.isArray(f) || !Array.isArray(v)) throw new Error('Invalid resource data from server');
        setFolders(f);
        setVideos(v);
      })
      .catch((err) => {
        if (alive && id === resourceFetchId.current) setError(err.message || 'Failed to load resources');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    acknowledgementsApi.getMine()
      .then((a) => {
        if (alive) setHasAcknowledged(Boolean(a.acknowledged || (a.acknowledgements || []).length));
      })
      .catch((err) => {
        console.error('Failed to load acknowledgement status:', err);
      });

    return () => { alive = false; resourceFetchId.current += 1; };
  }, []);

  /* ---------------- Derived data ---------------- */
  const allPolicies = useMemo(
    () => folders.flatMap((f) => (f.policies || []).map((p) => ({ ...p, _folder: f.name }))),
    [folders]
  );
  // Per-category totals are unaffected by search, matching the sidebar badges.
  const countOf = (id) => {
    if (id === 'all') return allPolicies.length + videos.length;
    if (id === 'documents') return allPolicies.length;
    if (id === 'policies') {
      const policiesCount = allPolicies.filter((p) => typeOfPolicy(p) === 'policies').length;
      const videosCount = videos.filter((v) => typeOfVideo(v) === 'policies').length;
      return policiesCount + videosCount;
    }
    if (id === 'videos') return videos.length;
    if (id === 'guides') {
      return allPolicies.filter((p) => typeOfPolicy(p) === 'guides').length
        + videos.filter((v) => typeOfVideo(v) === 'guides').length;
    }
    if (id === 'backgrounds') {
      return allPolicies.filter((p) => typeOfPolicy(p) === 'backgrounds').length;
    }
    if (id === 'tax-pf') {
      return allPolicies.filter((p) => typeOfPolicy(p) === 'tax-pf').length
        + videos.filter((v) => typeOfVideo(v) === 'tax-pf').length;
    }
    return allPolicies.filter((p) => typeOfPolicy(p) === id).length;
  };
  const resourceCategories = CATS;
  const categories = resourceCategories;

  const q = query.trim().toLowerCase();
  const matches = (text) => !q || String(text || '').toLowerCase().includes(q);

  // Decide which sections are in view for the active category
  let viewDocuments = [];
  let viewCompanyPolicies = [];
  let viewGuides = [];
  let viewBackgrounds = [];
  let viewTaxPf = [];
  let viewVideos = [];
  const docsSubtitle = 'Key HR documents & forms';
  const policiesOfType = (type) => allPolicies.filter((p) => typeOfPolicy(p) === type);
  const companyPoliciesForTab = policiesOfType('policies');
  const companyPolicyVideosForTab = videos.filter((v) => typeOfVideo(v) === 'policies');

  if (activeCat === 'all') {
    viewDocuments = policiesOfType('documents');
    viewCompanyPolicies = policiesOfType('policies');
    viewGuides = policiesOfType('guides');
    viewBackgrounds = policiesOfType('backgrounds');
    viewTaxPf = policiesOfType('tax-pf');
    viewVideos = videos;
  } else if (activeCat === 'documents') {
    viewDocuments = allPolicies;
  } else if (activeCat === 'policies') {
    // Company policy documents and videos are rendered in their matching tag folder below.
  } else if (activeCat === 'guides') {
    // User guide resources are rendered in tag folders below and may be documents or videos.
  } else if (activeCat === 'backgrounds') {
    // Virtual backgrounds are rendered in their matching tag folder below.
  } else if (activeCat === 'tax-pf') {
    // Tax and PF resources are rendered in their matching tag folder below.
  } else if (activeCat === 'videos') {
    viewVideos = videos;
  }

  // Search filter
  const filterPolicies = (items) => items.filter((p) => matches(p.title) || matches(p.description) || (p.tags || []).some((t) => matches(t?.name)));
  viewDocuments = filterPolicies(viewDocuments);
  viewCompanyPolicies = filterPolicies(viewCompanyPolicies);
  viewGuides = filterPolicies(viewGuides);
  viewBackgrounds = filterPolicies(viewBackgrounds);
  viewTaxPf = filterPolicies(viewTaxPf);
  viewVideos = viewVideos.filter((v) => matches(v.title) || matches(v.description) || (v.tags || []).some((t) => matches(t?.name)));

  // Sort documents
  const sortPolicies = (items) => {
    if (sort === 'recent') return [...items].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    if (sort === 'required') return [...items].sort((a, b) => (isRequired(b) ? 1 : 0) - (isRequired(a) ? 1 : 0));
    return [...items].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  };
  viewDocuments = sortPolicies(viewDocuments);
  viewCompanyPolicies = sortPolicies(viewCompanyPolicies);
  viewGuides = sortPolicies(viewGuides);
  viewBackgrounds = sortPolicies(viewBackgrounds);
  viewTaxPf = sortPolicies(viewTaxPf);
  viewVideos = [...viewVideos].sort((a, b) => (a.title || '').localeCompare(b.title || ''));

  const resourceFolders = (() => {
    if (activeCat !== 'all' && activeCat !== 'policies' && activeCat !== 'guides' && activeCat !== 'backgrounds' && activeCat !== 'tax-pf') return [];

    const groups = new Map();
    const tagsOrFallback = (resource, fallback) => {
      const tags = topicTags(resource);
      return tags.length > 0 ? tags : [{ ...fallback, isFallback: true }];
    };
    const addToFolder = (resource, kind, tagsForFolders) => {
      tagsForFolders.forEach((tag) => {
        const tagKey = normalizeResourceTag(tag.name).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        const key = tag.isFallback
          ? `fallback-${tag.id || tagKey || 'other'}`
          : activeCat === 'all'
            ? `tag-${tag.id || tagKey}`
            : `tag-name-${tagKey}`;
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            name: tag.isFallback || activeCat === 'policies' || activeCat === 'guides' || activeCat === 'backgrounds' || activeCat === 'tax-pf'
              ? String(tag.name).trim()
              : folderLabel(tag.name),
            isFallback: Boolean(tag.isFallback),
            items: [],
          });
        }
        groups.get(key).items.push({ kind, resource });
      });
    };

    if (activeCat === 'policies') {
      const policyDocuments = sortPolicies(filterPolicies(companyPoliciesForTab));
      const policyVideos = companyPolicyVideosForTab
        .filter((video) => matches('Company policies') || matches(video.title) || matches(video.description) || (video.tags || []).some((tag) => matches(tag?.name)))
        .sort((a, b) => (a.title || '').localeCompare(b.title || ''));

      policyDocuments.forEach((policy) => {
        addToFolder(policy, 'policy', tagsOrFallback(policy, TAB_FALLBACKS.policies));
      });
      policyVideos.forEach((video) => {
        addToFolder(video, 'video', tagsOrFallback(video, TAB_FALLBACKS.policies));
      });
    } else if (activeCat === 'guides') {
      const guidePolicies = sortPolicies(filterPolicies(
        allPolicies.filter((policy) => typeOfPolicy(policy) === 'guides')
      ));
      const guideVideos = videos
        .filter((video) => typeOfVideo(video) === 'guides')
        .filter((video) => matches('General user guides') || matches(video.title) || matches(video.description) || (video.tags || []).some((tag) => matches(tag?.name)))
        .sort((a, b) => (a.title || '').localeCompare(b.title || ''));

      guidePolicies.forEach((policy) => {
        addToFolder(policy, 'policy', tagsOrFallback(policy, TAB_FALLBACKS.guides));
      });
      guideVideos.forEach((video) => {
        addToFolder(video, 'video', tagsOrFallback(video, TAB_FALLBACKS.guides));
      });
    } else if (activeCat === 'backgrounds') {
      const backgroundResources = sortPolicies(filterPolicies(
        allPolicies.filter((policy) => typeOfPolicy(policy) === 'backgrounds')
      ));

      backgroundResources.forEach((policy) => {
        addToFolder(policy, 'policy', tagsOrFallback(policy, TAB_FALLBACKS.backgrounds));
      });
    } else if (activeCat === 'tax-pf') {
      const taxPfResources = sortPolicies(filterPolicies(
        allPolicies.filter((policy) => typeOfPolicy(policy) === 'tax-pf')
      ));

      taxPfResources.forEach((policy) => {
        addToFolder(policy, 'policy', tagsOrFallback(policy, TAB_FALLBACKS['tax-pf']));
      });
      videos
        .filter((video) => typeOfVideo(video) === 'tax-pf')
        .filter((video) => matches('Tax & PF assistance') || matches(video.title) || matches(video.description) || (video.tags || []).some((tag) => matches(tag?.name)))
        .forEach((video) => {
          addToFolder(video, 'video', tagsOrFallback(video, TAB_FALLBACKS['tax-pf']));
        });
    } else {
      const visiblePolicies = sortPolicies(filterPolicies(allPolicies));
      visiblePolicies.forEach((policy) => {
        const tags = topicTags(policy);
        const tagsForFolders = tags.length > 0
          ? tags
          : [{ id: 'other-documents', name: 'Other Documents', isFallback: true }];
        addToFolder(policy, 'policy', tagsForFolders);
      });
    }

    groups.forEach((folder) => {
      folder.items.sort((a, b) => (a.resource.title || '').localeCompare(b.resource.title || ''));
    });

    return [...groups.values()].sort((a, b) => {
      if (a.isFallback !== b.isFallback) return a.isFallback ? 1 : -1;
      return a.name.localeCompare(b.name);
    });
  })();

  // The portal starts directly with folders; recently added resources are not shown as a separate strip.
  const showRecent = false;
  const showBanner = hasAcknowledged === false && activeCat === 'all' && !q;
  const showDocuments = viewDocuments.length > 0;
  const showCompanyPolicies = viewCompanyPolicies.length > 0;
  const showGuides = viewGuides.length > 0;
  const showBackgrounds = viewBackgrounds.length > 0;
  const showTaxPf = viewTaxPf.length > 0;
  const showVideos = viewVideos.length > 0;
  const showEmpty = resourceFolders.length === 0 && !showDocuments && !showCompanyPolicies && !showGuides && !showBackgrounds && !showTaxPf && !showVideos && !showRecent;

  const activeCatObj = categories.find((c) => c.id === activeCat);
  const sectionTitle = activeCatObj ? activeCatObj.label : 'All resources';
  const folderResultCount = activeCat === 'policies' || activeCat === 'guides' || activeCat === 'backgrounds' || activeCat === 'tax-pf'
    ? new Set(resourceFolders.flatMap((folder) => folder.items.map((item) => `${item.kind}-${item.resource.id}`))).size
    : 0;
  const resultCount = viewDocuments.length + viewCompanyPolicies.length + viewGuides.length + viewBackgrounds.length + viewTaxPf.length + viewVideos.length + folderResultCount;

  const recent = useMemo(() => {
    const items = [
      ...allPolicies.map((p) => ({ kind: 'doc', id: p.id, title: p.title, date: p.created_at, meta: CATS.find((cat) => cat.id === typeOfPolicy(p))?.label || 'Document', ref: p })),
      ...videos.map((v) => ({ kind: 'video', id: v.id, title: v.title, date: v.created_at, meta: 'Video', ref: v })),
    ]
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
      .slice(0, 3);
    return items;
  }, [allPolicies, videos]);

  /* ---------------- Actions ---------------- */
  const policyUrl = (p) => isImageFile(p) ? policiesApi.downloadUrl(p.id) : policiesApi.viewUrl(p.id);
  const openDoc = (p) => {
    const url = policyUrl(p);
    if (isImageFile(p)) window.location.assign(url);
    else window.open(url, '_blank', 'noopener,noreferrer');
  };
  const openVideo = (v) => { setActiveVideo(v); setVideoPlaying(false); };
  // Auto-close the drawer after a selection only on small screens; on desktop
  // it stays open (it's a persistent, collapsible panel).
  const closeNavOnMobile = () => { if (typeof window !== 'undefined' && window.innerWidth <= 768) setNavOpen(false); };
  const goCat = (id) => { setActiveCat(id); setAdminView(null); closeNavOnMobile(); };
  const goAdmin = (id) => {
    setAdminView(id);
    setNavOpen(true);
    setProfileOpen(false);
  };

  const AdminComp = adminView && isAdmin ? ADMIN_COMPONENTS[adminView] : null;
  const adminLabel = ADMIN_ITEMS.find((a) => a.id === adminView)?.label || '';

  const openAck = () => {
    setAckError('');
    setAckOpen(true);
  };
  const confirmAck = async () => {
    if (ackSaving) return;

    try {
      setAckSaving(true);
      setAckError('');
      await acknowledgementsApi.acknowledge();
      setHasAcknowledged(true);
      setAckOpen(false);
    } catch (err) {
      setAckError(err.message || 'Failed to save acknowledgement');
    } finally {
      setAckSaving(false);
    }
  };

  /* ---------------- Render pieces ---------------- */
  const userName = user?.name || 'Employee';
  const userRole = user?.role || 'Employee';
  const renderPolicySection = (title, subtitle, items) => {
    if (!items.length) return null;

    return (
      <section className="hh-section">
        <div className="hh-section-head">
          <h2 className="hh-section-title">{title}</h2>
          {subtitle && <span className="hh-section-sub">{subtitle}</span>}
        </div>
        <div className="hh-doc-list">
          {items.map((d) => {
            const tag = d.tags?.[0]?.name || d._folder;
            const required = isRequired(d);
            return (
              <div key={d.id} className="hh-doc-row">
                <span className="hh-doc-icon"><Icon name="file" size={22} stroke={1.7} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, color: 'var(--hh-text-strong)', fontSize: 'var(--hh-text-md)' }}>{d.title}</span>
                    {tag && !isDefaultFolder(tag) && <span className="hh-tag-pill">{tag}</span>}
                    {required && (
                      <span className="hh-required-pill"><span className="hh-required-dot" />Required</span>
                    )}
                  </div>
                  <div className="hh-doc-meta">{docMeta(d)}</div>
                </div>
                <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <a className="hh-btn-outline" href={policyUrl(d)} {...(isImageFile(d) ? { download: '' } : { target: '_blank', rel: 'noopener noreferrer' })}>
                    <Icon name="external" size={16} stroke={1.75} />
                    {isImageFile(d) ? 'Download' : 'Open'}
                  </a>
                  <a className="hh-icon-btn" href={policiesApi.downloadUrl(d.id)} download title={`Download ${d.title}`} aria-label={`Download ${d.title}`}>
                    <Icon name="download" size={17} stroke={1.75} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  const renderVideoCard = (video) => {
    const category = video.tags?.[0]?.name || 'Video';
    return (
      <button
        key={video.id}
        type="button"
        className="hh-video-card"
        onClick={() => openVideo(video)}
        aria-label={`Play ${video.title}`}
      >
        <div className="hh-video-thumb">
          <span className="hh-video-cat">{category}</span>
          <span className="hh-video-play"><Icon name="play" size={22} fill /></span>
          {video.duration && <span className="hh-video-duration">{video.duration}</span>}
        </div>
        <div className="hh-video-body">
          <div className="hh-video-title">{video.title}</div>
        </div>
      </button>
    );
  };

  const toggleResourceFolder = (key) => {
    setExpandedResourceFolders((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const renderResourceFolders = () => {
    if ((activeCat !== 'all' && activeCat !== 'policies' && activeCat !== 'guides' && activeCat !== 'backgrounds' && activeCat !== 'tax-pf') || resourceFolders.length === 0) return null;

    const policyFolders = resourceFolders
      .map((folder) => ({
        ...folder,
        items: folder.items.filter(({ kind }) => kind !== 'video'),
      }))
      .filter((folder) => folder.items.length > 0);
    const assignedVideos = [...new Map(
      resourceFolders
        .flatMap((folder) => folder.items)
        .filter(({ kind }) => kind === 'video')
        .map(({ resource }) => [String(resource.id), resource])
    ).values()];
    const assignedVideoLabel = activeCatObj?.label || 'Video';

    return (
      <>
        {policyFolders.length > 0 && (
          <section className="hh-section hh-resource-folders">
            <div className="hh-section-head">
              <h2 className="hh-section-title">Resource folders</h2>
              <span className="hh-section-sub">Grouped guides &amp; references</span>
            </div>
            <h3 className="hh-resource-kind-heading">
              <Icon name="file" size={18} stroke={1.75} />
              Documents
            </h3>
            <div className="hh-folder-list">
              {policyFolders.map((folder) => {
                const isOpen = Boolean(q) || expandedResourceFolders.has(folder.key);
                const panelId = `resource-folder-${folder.key}`;
                return (
                  <div key={folder.key} className="hh-folder">
                    <button
                      type="button"
                      className="hh-folder-head"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => toggleResourceFolder(folder.key)}
                    >
                      <span className="hh-folder-icon"><Icon name="folder" size={23} stroke={1.75} /></span>
                      <span className="hh-folder-name">{folder.name}</span>
                      <span className="hh-folder-count">{folder.items.length} {folder.items.length === 1 ? 'item' : 'items'}</span>
                      <span className={`hh-folder-chev${isOpen ? ' is-open' : ''}`} aria-hidden="true">
                        <Icon name="chevronRight" size={20} stroke={2} />
                      </span>
                    </button>

                    {isOpen && (
                      <div id={panelId} className="hh-folder-body">
                        {folder.items.map(({ kind, resource }) => (
                          <div key={`${kind}-${resource.id}`} className="hh-folder-item">
                            <span
                              className={`hh-folder-item-icon is-${fileTypeOf(resource).toLowerCase()}`}
                              aria-label={`${fileTypeOf(resource)} document`}
                            >
                              <Icon name="file" size={19} stroke={1.8} />
                            </span>
                            <a
                              className="hh-folder-item-name hh-folder-item-link"
                              href={policyUrl(resource)}
                              {...(isImageFile(resource) ? { download: '' } : { target: '_blank', rel: 'noopener noreferrer' })}
                            >
                              {resource.title}
                            </a>
                            <span className={`hh-type-badge is-${fileTypeOf(resource).toLowerCase()}`}>{fileTypeOf(resource)}</span>
                            <a
                              className="hh-folder-dl"
                              href={policiesApi.downloadUrl(resource.id)}
                              download
                              title={`Download ${resource.title}`}
                              aria-label={`Download ${resource.title}`}
                            >
                              <Icon name="download" size={17} stroke={1.75} />
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {assignedVideos.length > 0 && (
          <section className="hh-section hh-assigned-videos" aria-label={`${assignedVideoLabel} videos`}>
            <h3 className="hh-resource-kind-heading">
              <Icon name="video" size={19} stroke={1.75} />
              Videos
            </h3>
            <div className="hh-video-grid hh-assigned-video-grid">
              {assignedVideos.map((video) => renderVideoCard(video))}
            </div>
          </section>
        )}
      </>
    );
  };

  const sidebar = (
    <aside className="hh-sidebar">
      <div className="hh-logo-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 8px 18px' }}>
        <img src="/logo.png" alt="i2e Consulting" style={{ height: 30, width: 'auto', display: 'block' }} />
      </div>

      <div className="hh-eyebrow-row" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px 4px' }}>
        <span className="hh-sidebar-eyebrow">{adminView ? 'Administration' : 'HR Hub'}</span>
        <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,.1)' }} />
      </div>

      <div className="hh-sidebar-label" style={{ padding: '14px 8px 6px' }}>{adminView ? 'Management' : 'Browse'}</div>

      <nav className="hh-nav">
        {adminView && (
          <button type="button" className="hh-nav-item hh-back-hub" onClick={() => goCat('all')}>
            <Icon name="chevronLeft" size={19} stroke={1.9} />
            <span className="hh-nav-label">Back to HR Hub</span>
          </button>
        )}

        {!adminView && resourceCategories.map((c) => {
          const active = !adminView && c.id === activeCat;
          return (
            <button
              key={c.id}
              type="button"
              className={`hh-nav-item${active ? ' is-active' : ''}`}
              aria-current={active ? 'page' : undefined}
              title={c.label}
              onClick={() => goCat(c.id)}
            >
              <Icon name={c.icon} size={19} stroke={1.75} />
              <span className="hh-nav-label">{c.label}</span>
              <span className="hh-nav-pill">{countOf(c.id)}</span>
            </button>
          );
        })}

        {isAdmin && (
          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              className={`hh-nav-item${adminView ? ' is-active' : ''}`}
              title="Admin Panel"
              onClick={() => {
                if (!adminView) goAdmin('users');
              }}
            >
              <Icon name="settings" size={19} stroke={1.6} />
              <span className="hh-nav-label">Admin Panel</span>
            </button>
            <div className="hh-admin-sub">
              {ADMIN_ITEMS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`hh-subnav-item${adminView === item.id ? ' is-active' : ''}`}
                  aria-current={adminView === item.id ? 'page' : undefined}
                  onClick={() => goAdmin(item.id)}
                >
                  <Icon name={item.icon} size={16} stroke={1.75} />
                  <span className="hh-nav-label">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      <div style={{ marginTop: 'auto', paddingTop: 18 }}>
        <div className="hh-user-card">
          <span className="hh-avatar">{getInitials(user?.name)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 'var(--hh-text-base)', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {userName}
            </div>
            <div style={{ fontSize: 'var(--hh-text-xs)', color: 'var(--hh-neutral-500)', textTransform: 'capitalize' }}>
              {userRole}
            </div>
          </div>
        </div>
        <button type="button" className="hh-signout" title="Sign out" onClick={() => logout()}>
          <Icon name="signout" size={17} stroke={1.75} />
          <span className="hh-signout-label">Sign out</span>
        </button>
      </div>
    </aside>
  );

  return (
    <div className={`hh-root${navOpen ? ' hh-nav-open' : ''}${adminView ? ' hh-admin-mode' : ''}`}>
      {sidebar}
      <div className="hh-scrim" onClick={() => setNavOpen(false)} />
      <main className="hh-main">
        <header className="hh-header">
          <div className="hh-top-brand">
            <span className="hh-top-logo" aria-label="i2e Consulting">
              <img src="/logo.png" alt="i2e Consulting" />
            </span>
            <span className="hh-top-brand-divider" aria-hidden="true" />
            <span className="hh-top-brand-title">HR Hub</span>
          </div>
          {adminView ? (
            <div className="hh-header-admin">
              <div className="hh-breadcrumb" style={{ marginBottom: 0 }}>
                <span>Employee portal</span>
                <Icon name="chevronRight" size={12} stroke={2} />
                <span>Admin panel</span>
                <Icon name="chevronRight" size={12} stroke={2} />
                <span style={{ color: 'var(--hh-text-body)', fontWeight: 600 }}>{adminLabel}</span>
              </div>
            </div>
          ) : (
            <>
              <div className="hh-header-title">
                <div className="hh-breadcrumb">
                  <span>Employee portal</span>
                  <Icon name="chevronRight" size={12} stroke={2} />
                  <span style={{ color: 'var(--hh-text-body)', fontWeight: 600 }}>HR Hub</span>
                </div>
                <div className="hh-title-row">
                  <h1 className="hh-title">{sectionTitle}</h1>
                  <span className="hh-count-pill">{resultCount}</span>
                </div>
              </div>

              {false && <div className="hh-header-search">
                <label className="hh-search">
                  <Icon name="search" size={18} />
                  <input
                    className="hh-search-input"
                    type="text"
                    placeholder="Search documents, guides and videos…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    aria-label="Search resources"
                  />
                </label>
              </div>}

              <div className="hh-header-actions">
                <label className="hh-select-wrap hh-header-sort" aria-label="Sort resources">
                  <svg className="hh-select-lead" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--hh-text-muted)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 11, pointerEvents: 'none' }}><path d="M4 5h16l-6.5 8v6l-3-2v-4z" /></svg>
                  <select className="hh-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                    <option value="name">Name (A–Z)</option>
                    <option value="recent">Recently updated</option>
                    <option value="required">Required first</option>
                  </select>
                  <svg className="hh-select-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--hh-text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 9l7 7 7-7" /></svg>
                </label>

                {hasAcknowledged === false && (
                  <button type="button" className="hh-btn-primary" onClick={openAck}>
                    <Icon name="clipboardCheck" size={17} stroke={1.9} />
                    <span className="hh-action-label">Acknowledgement</span>
                    <span className="hh-ack-badge">2</span>
                  </button>
                )}
                {isAdmin && (
                  <button type="button" className="hh-top-admin" onClick={() => goAdmin('users')}>
                    <Icon name="settings" size={17} stroke={1.8} />
                    <span className="hh-action-label">Admin Panel</span>
                  </button>
                )}
              </div>
            </>
          )}
          {!adminView && <div className="hh-profile-menu" ref={profileMenuRef}>
            <button
              type="button"
              className="hh-profile-trigger"
              aria-haspopup="menu"
              aria-expanded={profileOpen}
              onClick={() => setProfileOpen((open) => !open)}
            >
              <span className="hh-profile-name">{userName}</span>
              <span className="hh-profile-avatar">{getInitials(userName)}</span>
              <span className={`hh-profile-caret${profileOpen ? ' is-open' : ''}`} aria-hidden="true" />
            </button>
            {profileOpen && (
              <div className="hh-profile-dropdown" role="menu">
                  <div className="hh-profile-summary">
                    <span className="hh-profile-avatar hh-profile-avatar-large">{getInitials(userName)}</span>
                    <span className="hh-profile-details">
                      <strong>{userName}</strong>
                      <small>{user?.email || userRole}</small>
                    </span>
                  </div>
                  <button type="button" role="menuitem" onClick={() => { setProfileOpen(false); logout(); }}>
                    <Icon name="signout" size={17} stroke={1.8} />
                    Sign out
                  </button>
              </div>
            )}
          </div>}
        </header>

        <div className="hh-scroll">
          <div className="hh-content">
            {!adminView && (
              <div className="hh-header-search">
                <label className="hh-search">
                  <Icon name="search" size={18} />
                  <input
                    className="hh-search-input"
                    type="text"
                    placeholder="Search documents, guides and videos..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    aria-label="Search resources"
                  />
                </label>
              </div>
            )}
            {AdminComp ? (
              <AdminComp
                onResourcesChanged={refreshPublishedResources}
                onManageTags={() => setAdminView('tags')}
              />
            ) : (
              <>
                {loading ? (
                  <p className="hh-resource-status" role="status">Loading resource folders…</p>
                ) : error ? (
                  <ErrorState message={error} />
                ) : (
                  <>
                {showBanner && (
                  <div
                    className="hh-banner"
                    style={{
                      background: 'var(--hh-warning-surface)',
                      border: '1px solid rgba(245,158,11,.3)',
                    }}
                  >
                    <span style={{ width: 40, height: 40, flex: 'none', borderRadius: '50%', background: 'var(--hh-warning-500)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon name="alert" size={21} stroke={1.9} />
                    </span>
                    <div className="hh-banner-text" style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: 'var(--hh-text-strong)', fontSize: 'var(--hh-text-md)' }}>
                        You have 2 required policies to acknowledge
                      </div>
                      <span className="hh-banner-copy">Please review and confirm you have read the mandatory HR documents.</span>
                    </div>
                    <button type="button" className="hh-btn-primary" style={{ height: 38, boxShadow: 'none' }} onClick={openAck}>
                      Review &amp; acknowledge
                    </button>
                  </div>
                )}

                {showRecent && recent.length > 0 && (
                  <section className="hh-section">
                    <div className="hh-section-head">
                      <h2 className="hh-section-title">Recently added</h2>
                      <span className="hh-section-sub">The latest documents and videos</span>
                    </div>
                    <div className="hh-recent-grid">
                      {recent.map((r) => (
                        <button
                          key={`${r.kind}-${r.id}`}
                          type="button"
                          className="hh-recent-card"
                          onClick={() => (r.kind === 'video' ? openVideo(r.ref) : openDoc(r))}
                        >
                          <span
                            style={{
                              width: 42, height: 42, flex: 'none', borderRadius: 'var(--hh-radius-md)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: r.kind === 'video' ? 'var(--hh-brand-subtle)' : 'var(--hh-danger-surface)',
                              color: r.kind === 'video' ? 'var(--hh-brand)' : 'var(--hh-danger)',
                            }}
                          >
                            <Icon name={r.kind === 'video' ? 'video' : 'file'} size={21} stroke={1.75} />
                          </span>
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: 'block', fontWeight: 600, color: 'var(--hh-text-strong)', fontSize: 'var(--hh-text-base)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {r.title}
                            </span>
                            <span style={{ display: 'block', fontSize: 'var(--hh-text-xs)', color: 'var(--hh-text-muted)', marginTop: 2 }}>
                              {r.meta}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </section>
                )}

                {renderResourceFolders()}
                  </>
                )}

                {!loading && !error && (
                  <>
                {activeCat !== 'all' && renderPolicySection('Documents', docsSubtitle, viewDocuments)}
                {activeCat !== 'all' && renderPolicySection('Company Policies', 'Published company policies', viewCompanyPolicies)}
                {activeCat !== 'all' && renderPolicySection('User Guides', 'How-to guides and references', viewGuides)}
                {activeCat !== 'all' && renderPolicySection('Virtual Backgrounds', 'Approved meeting backgrounds', viewBackgrounds)}

                {showVideos && (
                  <section className="hh-section">
                    <div className="hh-section-head">
                      <h2 className="hh-section-title">Video tutorials</h2>
                      <span className="hh-section-sub">Step-by-step walkthroughs</span>
                    </div>
                    <div className="hh-video-grid">
                      {viewVideos.map((video) => renderVideoCard(video))}
                    </div>
                  </section>
                )}

                {showEmpty && (
                  <div className="hh-empty">
                    <span className="hh-empty-icon"><Icon name="search" size={32} stroke={1.75} /></span>
                    <h3 style={{ margin: 0, fontFamily: 'var(--hh-font-display)', fontWeight: 700, fontSize: 'var(--hh-text-xl)', color: 'var(--hh-text-strong)' }}>
                      No resources found
                    </h3>
                  </div>
                )}

                {activeCat === 'all' && !q && <SupportHeroes />}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {activeVideo && (
        <VideoModal
          video={activeVideo}
          playing={videoPlaying}
          onPlay={() => setVideoPlaying(true)}
          onClose={() => setActiveVideo(null)}
        />
      )}

      {ackOpen && (
        <AckModal
          saving={ackSaving}
          error={ackError}
          onConfirm={confirmAck}
          onClose={() => setAckOpen(false)}
        />
      )}
    </div>
  );
}

/* ============================================================
   Sub-components
   ============================================================ */
function SupportHeroes() {
  const rows = [
    ['HR - Payroll & Compliance Management', 'i2e Beehive Attendance & Leave, Timesheet, Working Hours, Bajaj Mediclaim, HDFC bank account opening, Confirmation, Appraisals', [
      { name: 'Harshika Tibrewal', designation: 'Sr. HR Executive', email: 'harshika.tibrewal@i2econsulting.com' },
      { name: 'Reshma Narkar', designation: 'Sr. Manager - HR', email: 'reshma.narkar@i2econsulting.com' },
    ]],
    ['Payroll', 'Deductions in Salary, Arrears, Salary Slip', [
      { name: 'Neeta Pawaskar', designation: 'Sr. Accountant', email: 'neeta.pawaskar@i2econsulting.com' },
      { name: 'Reshma Narkar', designation: 'Sr. Manager - HR', email: 'reshma.narkar@i2econsulting.com' },
    ]],
    ['HR - Learning & Development, Employee Engagement', 'LinkedIn Learning, Upskill (Disprz), Pats, Office Events', [
      { name: 'Sonam Shrimal', designation: 'Sr. Manager – L & D & Employee Engagement', email: 'sonam.shrimal@i2econsulting.com' },
    ]],
    ['HR - Talent Management', 'Openings in i2e, Employee Referral', [
      { name: 'Mithila Rane', designation: 'Sr. Manager - Talent Acquisition', email: 'mithila.rane@i2econsulting.com' },
    ]],
    ['Admin', 'Travel / Hotel Booking/Meeting / conference room booking', [
      { name: 'Sridhar Ramesh', designation: 'Admin Executive', email: 'sridhar.ramesh@i2econsulting.com' },
    ]],
    ['IT', 'System programs and matters related to hardware', [
      { name: 'Abhijit Raorane', designation: 'Jr. System Administrator', email: 'abhijit.raorane@i2econsulting.com' },
    ]],
    ['Cyber Security', 'Phishing Emails', [
      { name: 'Shashank Gidbidi', designation: 'Technical Lead', email: 'shashank.gidbidi@i2econsulting.com' },
    ]],
    ['Accounts & Finance', 'Investment Declaration, PF, Interns Attendance & Invoicing', [
      { name: 'Vikram Nagaonkar', designation: 'Sr. Accounts Executive', email: 'vikram.n@i2econsulting.com' },
      { name: 'Prashant Morekar', designation: 'Manager-Finance & Accounts', email: 'prashant.morekar@i2econsulting.com' },
      { name: 'Ashutosh Dholam', designation: 'Sr. Accounts Executive', email: 'ashutosh.dholam@i2econsulting.com' },
    ]],
  ];
  return <section className="hh-support">
    <div className="hh-section-head"><h2 className="hh-section-title">i2e Support Heroes</h2><span className="hh-section-sub">Who to contact, by query type</span></div>
    <div className="hh-support-table">
      <div className="hh-support-head"><span>Team</span><span>Queries On</span><span>Contact Person</span><span>Designation</span><span>Actions</span></div>
      {rows.map(([team, queries, contacts]) => <div className="hh-support-row" key={team}>
        <strong>{team}</strong><span>{queries}</span>
        <div className="hh-support-contacts">
          {contacts.map(({ name, designation, email }) => <div className="hh-support-contact" key={name}>
            <b><i>{getInitials(name)}</i>{name}</b>
            <span>{designation}</span>
            <span className="hh-support-actions">
              <a href={`msteams://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(email)}`} aria-label={`Message ${name} in Microsoft Teams`} title={`Chat with ${name} in Microsoft Teams`} className="hh-support-action hh-support-action-teams"><TeamsLogo /></a>
              <a href={`mailto:${email}`} aria-label={`Compose an email to ${name}`} title={`Compose an email to ${name}`} className="hh-support-action hh-support-action-outlook"><OutlookLogo /></a>
            </span>
          </div>)}
        </div>
      </div>)}
    </div>
  </section>;
}

function TeamsLogo() {
  return <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
    <path fill="#5059c9" d="M11 8.5h12.3A3.7 3.7 0 0 1 27 12.2v11.1a3.7 3.7 0 0 1-3.7 3.7H11z" /><circle cx="22.5" cy="6.5" r="3.5" fill="#7b83eb" /><circle cx="28.2" cy="12.4" r="2.3" fill="#7b83eb" /><path fill="#7b83eb" d="M22.5 13.5h5.7a2.8 2.8 0 0 1 2.8 2.8v5.2a2.8 2.8 0 0 1-2.8 2.8h-5.7z" /><path fill="#7b83eb" d="M3 10.3A2.3 2.3 0 0 1 5.3 8h11.4A2.3 2.3 0 0 1 19 10.3v11.4a2.3 2.3 0 0 1-2.3 2.3H5.3A2.3 2.3 0 0 1 3 21.7z" /><path fill="#fff" d="M15.8 12.1H7v2.1h3.2v6.4h2.5v-6.4h3.1z" />
  </svg>;
}

function OutlookLogo() {
  return <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
    <path fill="#0078d4" d="M13.5 6.5h12A3.5 3.5 0 0 1 29 10v12a3.5 3.5 0 0 1-3.5 3.5h-12z" /><path fill="#28a8ea" d="m16 9 10.5-3v17L16 26z" /><path fill="#50d9ff" d="M16 12h10.5v11H16z" /><path fill="#0078d4" d="M3 8.2A2.2 2.2 0 0 1 5.2 6h11.6A2.2 2.2 0 0 1 19 8.2v15.6a2.2 2.2 0 0 1-2.2 2.2H5.2A2.2 2.2 0 0 1 3 23.8z" /><path fill="#fff" d="M11 10.5c-3 0-4.8 2.1-4.8 5.5s1.8 5.5 4.8 5.5 4.8-2.1 4.8-5.5-1.8-5.5-4.8-5.5m0 8.7c-1.3 0-2.1-1.1-2.1-3.2s.8-3.2 2.1-3.2 2.1 1.1 2.1 3.2-.8 3.2-2.1 3.2" />
  </svg>;
}

function ErrorState({ message }) {
  return (
    <div className="hh-empty">
      <span className="hh-empty-icon" style={{ background: 'var(--hh-danger-surface)', color: 'var(--hh-danger)' }}>
        <Icon name="alert" size={32} stroke={1.9} />
      </span>
      <h3 style={{ margin: 0, fontFamily: 'var(--hh-font-display)', fontWeight: 700, fontSize: 'var(--hh-text-xl)', color: 'var(--hh-text-strong)' }}>
        Failed to load resources
      </h3>
      <p style={{ margin: '8px 0 18px', fontSize: 'var(--hh-text-md)', color: 'var(--hh-text-muted)', maxWidth: 380 }}>{message}</p>
      <button type="button" className="hh-btn-primary" style={{ boxShadow: 'none' }} onClick={() => window.location.reload()}>
        Try again
      </button>
    </div>
  );
}

function VideoModal({ video, playing, onPlay, onClose }) {
  const embedUrl = getEmbedUrl(video.video_url);
  const proxyUrl = videosApi.viewUrl(video.id);
  const cat = video.tags?.[0]?.name || 'Video';
  const openUrl = embedUrl ? video.video_url : proxyUrl;
  const stop = (e) => e.stopPropagation();

  return (
    <div className="hh-overlay" onClick={onClose}>
      <div className="hh-modal hh-modal-video" onClick={stop}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid var(--hh-border-subtle)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--hh-font-display)', fontWeight: 700, fontSize: 'var(--hh-text-lg)', color: 'var(--hh-text-strong)' }}>{video.title}</div>
            <div style={{ fontSize: 'var(--hh-text-sm)', color: 'var(--hh-text-muted)', marginTop: 1 }}>
              {cat}{video.duration ? `  ·  ${video.duration}` : ''}
            </div>
          </div>
          <button type="button" className="hh-modal-close" aria-label="Close" onClick={onClose}>
            <Icon name="close" size={19} stroke={2} />
          </button>
        </div>

        <div className="hh-modal-player">
          {playing ? (
            embedUrl ? (
              <iframe src={`${embedUrl}?autoplay=1`} title={video.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            ) : (
              <video src={proxyUrl} controls autoPlay />
            )
          ) : (
            <>
              <button type="button" className="hh-play-lg" aria-label="Play video" onClick={onPlay}>
                <Icon name="play" size={30} fill />
              </button>
              <span style={{ fontSize: 'var(--hh-text-sm)', color: 'var(--hh-neutral-400)' }}>Click to play the tutorial</span>
            </>
          )}
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', justifyContent: 'flex-end' }}>
          {openUrl && (
            <a className="hh-btn-outline" style={{ height: 40 }} href={openUrl} target="_blank" rel="noopener noreferrer">
              <Icon name="external" size={16} stroke={1.75} />
              Open in new tab
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function AckModal({
  saving,
  error,
  onConfirm,
  onClose
}) {
  const stop = (e) => e.stopPropagation();
  return (
    <div className="hh-overlay" onClick={onClose}>
      <div className="hh-modal hh-modal-ack" onClick={stop}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '20px 22px', borderBottom: '1px solid var(--hh-border-subtle)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'var(--hh-font-display)', fontWeight: 700, fontSize: 'var(--hh-text-lg)', color: 'var(--hh-text-strong)' }}>
              Acknowledge HR Hub
            </div>
          </div>
          <button type="button" className="hh-modal-close" aria-label="Close" onClick={onClose} style={{ width: 36, height: 36 }}>
            <Icon name="close" size={18} stroke={2} />
          </button>
        </div>

        <div>
          <div style={{ padding: '30px 24px', textAlign: 'center' }}>
            <span style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--hh-brand-subtle)', color: 'var(--hh-brand)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <Icon name="check" size={28} stroke={2.1} />
            </span>
            <h3 style={{ margin: 0, fontFamily: 'var(--hh-font-display)', fontWeight: 700, fontSize: 'var(--hh-text-xl)', color: 'var(--hh-text-strong)' }}>
              Confirm acknowledgement
            </h3>
            <p style={{ margin: '8px auto 0', maxWidth: 380, fontSize: 'var(--hh-text-md)', color: 'var(--hh-text-muted)', lineHeight: 1.5 }}>
              I acknowledge that I have reviewed the HR Hub information available to me. This confirmation will be recorded once and HR will be notified.
            </p>
          </div>
          {error && (
            <div style={{ padding: '0 22px 12px', color: 'var(--hh-danger)', fontSize: 'var(--hh-text-sm)' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, padding: '16px 22px', borderTop: '1px solid var(--hh-border-subtle)', background: 'var(--hh-neutral-25)' }}>
            <button type="button" className="hh-btn-outline" style={{ height: 40 }} disabled={saving} onClick={onClose}>Cancel</button>
            <button type="button" className="hh-btn-primary" style={{ boxShadow: 'none' }} disabled={saving} onClick={onConfirm}>
              {saving ? 'Submitting...' : 'Acknowledge'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HRHub;
